import * as React from "react";
import { cn } from "../utils/cn";
import { Heading, Mono, Text } from "../primitives/typography";

const detailColorMap = {
    default: "text-muted-foreground",
    green: "text-success",
    red: "text-destructive",
    yellow: "text-warning",
} as const;

export type QuizStatCardDetailColor = keyof typeof detailColorMap;

export interface QuizStatCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "content"> {
    label: string;
    value?: string | number;
    detail?: string;
    detailPrefix?: string;
    detailColor?: QuizStatCardDetailColor;
    standalone?: boolean;
    content?: React.ReactNode;
}

const QuizStatCard = React.forwardRef<HTMLDivElement, QuizStatCardProps>(
    (
        {
            className,
            label,
            value,
            detail,
            detailPrefix,
            detailColor = "default",
            standalone = true,
            content,
            ...props
        },
        ref,
    ) => (
        <div
            ref={ref}
            className={cn(
                "flex flex-col gap-1",
                standalone && "border-border bg-card rounded-2xl border p-5",
                className,
            )}
            {...props}
        >
            <Mono size="xs" muted className="tracking-wider uppercase">
                {label}
            </Mono>
            {content ? (
                <div className="pt-2">{content}</div>
            ) : (
                <>
                    <Heading level={3}>{value}</Heading>
                    <Text
                        as="span"
                        size="xs"
                        className={detailPrefix ? "text-muted-foreground" : detailColorMap[detailColor]}
                    >
                        {detail}
                    </Text>
                </>
            )}
        </div>
    ),
);
QuizStatCard.displayName = "QuizStatCard";

export interface QuizStatCardGroupProps extends React.HTMLAttributes<HTMLDivElement> {}

const QuizStatCardGroup = React.forwardRef<HTMLDivElement, QuizStatCardGroupProps>(
    ({ className, children, ...props }, ref) => (
        <div
            ref={ref}
            className={cn(
                "border-border bg-card rounded-2xl border",
                "divide-border flex flex-col divide-y",
                "sm:flex-row sm:divide-x sm:divide-y-0",
                className,
            )}
            {...props}
        >
            {React.Children.map(children, (child) =>
                React.isValidElement(child) ? (
                    <div className="min-w-0 flex-1 p-5">
                        {React.cloneElement(child as React.ReactElement<QuizStatCardProps>, { standalone: false })}
                    </div>
                ) : null,
            )}
        </div>
    ),
);
QuizStatCardGroup.displayName = "QuizStatCardGroup";

export { QuizStatCard, QuizStatCardGroup };
